/**
 * @file getAnswer.ts
 * Exports a method that fetches a question object for a given answerId.
 */
import { any, filter, head, prop, propEq, pipe } from 'ramda';
import { getQuestions } from '.';
import type { AppState, QuestionData } from '../types';

/**
 * Helper method that returns a question object for a given answerId.
 *
 * Each question contains multiple answers. Answer ids are unique across all
 * answers in the set of questions. This method finds the answer for a given
 * answer id and returns the parent question object.
 *
 * @param {AppState} appState
 *   The state object shared across the application.
 * @param {string} id
 *   The id of an answer within the source data.
 *
 * @returns {QuestionData|undefined}
 *   Answer object, or undefined.
 */
export default (appState: AppState, id: string): QuestionData => {
  const hasAnswerId = any(propEq('id', id));
  return pipe(
    // Get a list of all question data.
    getQuestions,
    // Filter the list to questions that have an answer with the given id.
    filter(pipe(prop('answers'), hasAnswerId)),
    // Return the first matching question.
    head,
  )(appState);
};
